import { gql } from "@apollo/client";
import _ from "lodash";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  BookTextIcon,
  BuildingIcon,
  CalendarDaysIcon,
  DollarSignIcon,
  GitBranchIcon,
  TableIcon,
  TextAlignStartIcon,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import {
  JournalEntryFragment,
  JournalEntryLinesQuery,
  JournalEntryStateEnum,
} from "@/__generated__/graphql";
import { TableColumnType } from "@/components/@types";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { FieldTable } from "@/components/organisms/Field";
import {
  useNodeField,
  useNodeLayoutStore,
} from "@/components/organisms/Node/Node.hooks";
import { reconcileServerStateWithPendingOperations } from "@/components/organisms/Node/Node.store";
import { Queries } from "@/constants";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { EnumColors, Values } from "@/constants/values";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { Utils } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

export function journalEntryStateColor(
  state: JournalEntryStateEnum | null | undefined,
) {
  if (state === JournalEntryStateEnum.Draft) return EnumColors.GREY_1;
  if (state === JournalEntryStateEnum.Posted) return EnumColors.BLUE;
  if (state === JournalEntryStateEnum.Pending) return EnumColors.RED;
  return EnumColors.GREY_1;
}

type JournalEntryLines = Extract<
  NonNullable<JournalEntryLinesQuery["node"]>,
  { __typename?: "JournalEntryObject" }
>;
export type JournalEntryLine = JournalEntryLines["transactions"][number];

/** Panel store shape once ``JOURNAL_ENTRY_LINES_QUERY`` has hydrated the lines. */
type JournalEntryWithLines = JournalEntryFragment & {
  transactions?: JournalEntryLine[] | null;
};

function useHydrateJournalEntryLines(
  journalEntryId: string | undefined | null,
) {
  const store = useNodeLayoutStore();
  const { data, loading, refetch } = useBackendQuery(
    Queries.JOURNAL_ENTRY_LINES_QUERY,
    {
      variables: { id: journalEntryId! },
      skip: !journalEntryId,
      fetchPolicy: "cache-first",
    },
  );

  const applyLines = useCallback(
    (node: unknown) => {
      const journalEntry = node as JournalEntryLines | null | undefined;
      if (!journalEntry || journalEntry.__typename !== "JournalEntryObject") {
        return;
      }
      const state = store.getState();
      if (state.interactionLockCount > 0) {
        return;
      }
      const transactions = journalEntry.transactions ?? [];
      const reconciled = reconcileServerStateWithPendingOperations(
        {
          ...(state.fieldValuesInitial as Record<string, unknown>),
          transactions,
        },
        state.pendingFieldOperations,
      );
      store.setState({
        fieldValuesInitial: {
          ...state.fieldValuesInitial,
          transactions,
        },
        fieldValues: {
          ...state.fieldValues,
          transactions: reconciled.fieldValues.transactions,
        },
        pendingFieldOperations: reconciled.pendingFieldOperations,
      });
    },
    [store],
  );

  // First load only. After that the store is authoritative: a save returns the
  // journal entry without its lines, so re-reading this query's cache would
  // undo the edit that was just saved.
  useLayoutEffect(() => {
    if (
      (store.getState().fieldValuesInitial as JournalEntryWithLines)
        ?.transactions != null
    ) {
      return;
    }
    applyLines(data?.node);
  }, [applyLines, data, store]);

  // A save moves ``updatedAt``: that is the signal the server has a newer answer
  // than the cache. Apply what the refetch itself returns.
  const journalEntryUpdatedAt = store(
    (state) => (state.fieldValues as { updatedAt?: string })?.updatedAt,
  );
  const lastUpdatedAtRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = lastUpdatedAtRef.current;
    lastUpdatedAtRef.current = journalEntryUpdatedAt;
    if (
      !journalEntryId ||
      !data ||
      !previous ||
      previous === journalEntryUpdatedAt
    ) {
      return;
    }
    void refetch().then((result) => applyLines(result.data?.node));
  }, [applyLines, journalEntryId, data, journalEntryUpdatedAt, refetch]);

  return { loading };
}

function JournalEntryCard({
  data,
  actions: _actions,
  isLoading = false,
}: ObjectCardProps<JournalEntryFragment>) {
  const totalRaw = data?.total ?? data?.subtotal;
  const total =
    totalRaw != null && totalRaw !== ""
      ? Utils.fCurrency(Number(totalRaw))
      : null;
  const createdAt = data?.createdAt ? new Date(data.createdAt) : null;
  const state = data?.journalEntryState;

  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      {total && (
        <Typography level="h4" className="[line-height:1.1]">
          <Skeleton loading={isLoading} variant="inline">
            {total}
          </Skeleton>
        </Typography>
      )}

      {createdAt && (
        <Typography level="body-sm" className="text-muted-foreground">
          <Skeleton loading={isLoading} variant="inline">
            {Utils.fDate(createdAt)}
          </Skeleton>
        </Typography>
      )}

      <div className="mt-auto flex gap-1 flex-wrap">
        {state && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={_.startCase(state.toString().toLowerCase())}
              enumColor={journalEntryStateColor(state)}
              size="sm"
            />
          </Skeleton>
        )}
      </div>
    </div>
  );
}

function JournalEntryTransactionsTab({ size }: { size: "sm" | "md" }) {
  const store = useNodeLayoutStore();
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    "transactions",
  );
  // ``useNodeField`` narrows ``fieldValues`` to the field's own value keys, so
  // the journal entry id has to come from the store.
  const journalEntryId = store(
    useShallow(
      (state) => (state.fieldValues as JournalEntryFragment | undefined)?.id,
    ),
  );
  const { loading } = useHydrateJournalEntryLines(journalEntryId);

  if (!fieldDefinition || fieldDefinition.type !== "table") {
    return null;
  }

  const value = (fieldDefinition.getValue(fieldValues) ??
    []) as JournalEntryLine[];
  const valueOptions = (fieldDefinition.getValueOptions?.(fieldValues) ?? {
    columns: [],
  }) as { columns: TableColumnType[] };

  return (
    <div className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden">
      <FieldTable
        value={value}
        valueOptions={valueOptions}
        events={fieldEvents}
        cellSize={size}
        isLoading={loading && value.length === 0}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

const object: ObjectType<JournalEntryFragment> = {
  humanName: "journal entry",
  inputKey: "journalEntry",
  typename: "JournalEntryObject",
  report: {
    source: "journal_entries",
    description: "The entries the books are written in",
    asks: "How much was posted to each account last month?",
  },
  ReactIcon: BookTextIcon,
  isEntity: false,
  titleFieldKey: "description",
  Card: JournalEntryCard,
  placeholder: {
    __typename: "JournalEntryObject",
    id: btoa("JournalEntryObject:placeholder"),
    identifier: "placeholder",
    description: "Office supplies from Northwind Supply",
    total: "248.60",
    createdAt: "2026-01-15T10:00:00Z",
    journalEntryState: JournalEntryStateEnum.Posted,
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as JournalEntryFragment,
  getTitle: (data) => {
    return data?.description;
  },
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  groups: [
    {
      id: "transactions",
      label: "Transactions",
      isVisible: true,
      component: JournalEntryTransactionsTab,
      flush: true,
      card: {
        isLabelVisible: false,
        flex: 1,
        isFlush: true,
      },
    },
  ],
  getReferenceValueOptions: (entityIdentifiers) => {
    return {
      query: gql`
        query JournalEntryList($entityIdentifiers: [String!]) {
          journalEntries(entityIdentifiers: $entityIdentifiers) {
            data {
              ...JournalEntry
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers: entityIdentifiers,
        },
      },
      onRenderOption: (option: JournalEntryFragment) => {
        return <>{option.description}</>;
      },
      mutationOptions: {
        createTypename: "JournalEntryObject",
      },
    };
  },
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Journal Entry",
      type: "reference",
      ReactIcon: iconsByTypename.JournalEntryObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "JournalEntryObject",
          name: data?.description,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.entity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("JournalEntryObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "createdAt",
      label: "Date",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.createdAt,
      getValue: (data) => data?.createdAt,
      fieldProps: {
        headerFieldType: "date",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "total",
      linkedValuesKeys: ["balance", "transactions"],
      label: "Balance",
      type: "currency",
      ReactIcon: DollarSignIcon,
      asString: () => "",
      getValue: (data) => {
        const lines = (data as JournalEntryWithLines)?.transactions;
        if (!lines) {
          return data?.total != null ? `${data.total}` : "0";
        }
        return `${lines.reduce((acc, transaction) => {
          const debit = transaction?.debit ? parseFloat(transaction.debit) : 0;
          const credit = transaction?.credit
            ? parseFloat(transaction.credit)
            : 0;
          return acc + debit - credit;
        }, 0)}`;
      },
      createProps: {
        isRequired: false,
        isEditable: false,
      },
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.description,
      getValue: (data) => {
        return data?.description;
      },
      getValueOptions: () => ({
        placeholder: "Cash payment for invoice 123, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "transactions",
      label: "Transactions",
      type: "table",
      listInputFormat: "array",
      ReactIcon: TableIcon,
      groupId: "transactions",
      asString: () => "",
      getValue: (data) => (data as JournalEntryWithLines)?.transactions || [],
      getValueOptions: () => ({
        columns: [
          {
            label: "Description",
            type: "string",
            width: 100,
            headerFieldType: "string",
            objectKey: "description",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => row?.description || "",
            getValueOptions: () => ({
              placeholder: "Cash payment for invoice 123",
            }),
          },
          {
            label: "Account",
            type: "reference",
            width: 100,
            headerFieldType: "string",
            objectKey: "account",
            inputKey: "chartedAccount",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => ({
              __typename: "ChartedAccountObject",
              id: row?.account?.id,
              identifier: row?.account?.identifier,
              name: row?.account?.name,
            }),
            getValueForApi: (row: any) => ({
              identifier: row?.account?.identifier,
            }),
            getValueOptions: () =>
              getReferenceValueOptionsByTypename("ChartedAccountObject")(),
          },
          {
            label: "Debit",
            type: "currency",
            width: 100,
            headerFieldType: "number",
            objectKey: "debit",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => (row?.credit != null ? row?.debit : null),
            getValueOptions: () => ({
              placeholder: "$0",
            }),
            preprocessOnChange: (key, value, data) => {
              if (value !== null && value !== undefined && value !== "") {
                data.credit = "";
              }
              return data;
            },
          },
          {
            label: "Credit",
            type: "currency",
            width: 100,
            headerFieldType: "number",
            objectKey: "credit",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => (row?.debit != null ? row?.credit : null),
            getValueOptions: () => ({
              placeholder: "$0",
            }),
            preprocessOnChange: (key, value, data) => {
              if (value !== null && value !== undefined && value !== "") {
                data.debit = "";
              }
              return data;
            },
          },
          {
            label: "Tax",
            type: "reference",
            width: 100,
            headerFieldType: "string",
            objectKey: "tax",
            inputKey: "tax",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => ({
              __typename: "TaxObject",
              name: row?.tax?.name,
              id: row?.tax?.id,
              identifier: row?.tax?.identifier,
            }),
            getValueForApi: (row: any) => ({
              identifier: row?.tax?.identifier,
            }),
            getValueOptions: () =>
              getReferenceValueOptionsByTypename("TaxObject")(),
          },
          {
            label: "Document",
            type: "reference",
            width: 140,
            headerFieldType: "string",
            objectKey: "document",
            inputKey: "paymentFor",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => {
              const document = row?.document ?? row?.paymentFor?.[0] ?? null;
              if (!document) {
                return {
                  __typename: "DocumentObject",
                  name: null,
                  id: null,
                  identifier: null,
                };
              }
              return {
                __typename: "DocumentObject",
                name: document.name || document.reference || "",
                id: document.id,
                identifier: document.identifier,
              };
            },
            getValueOptions: () => ({
              isNullable: true,
              ...getReferenceValueOptionsByTypename("DocumentObject")([]),
            }),
          },
          {
            label: "Tags",
            type: "references",
            width: 100,
            headerFieldType: "references",
            objectKey: "tags",
            isDisabled: false,
            isSearchable: false,
            getValue: (row: any) => row?.tags || [],
            getValueOptions: () =>
              getReferenceValueOptionsByTypename("TagObject")([]),
          },
        ],
      }),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
    {
      objectKey: "subtotal",
      label: "Subtotal",
      type: "number",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.subtotal,
      getValue: (data) => data?.subtotal,
      // createProps: {
      //   isRequired: false,
      //   isEditable: false,
      // },
      // updateProps: {
      //   isRequired: true,
      //   isEditable: true,
      // },
      fieldProps: {
        headerFieldType: "number",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "journalEntryState",
      label: "State",
      type: "enum",
      ReactIcon: GitBranchIcon,
      asString: (data) => data?.journalEntryState,
      getValue: (data) => data?.journalEntryState,
      getValueOptions: () => ({
        enum: _.map(JournalEntryStateEnum, (value, key) => ({ key, value })),
        getColors: (mode) => ({
          [JournalEntryStateEnum.Draft]: EnumColors.GREY_1[mode],
          [JournalEntryStateEnum.Posted]: EnumColors.BLUE[mode],
          [JournalEntryStateEnum.Pending]: EnumColors.RED[mode],
        }),
      }),
      // createProps: {
      //   isRequired: false,
      //   isEditable: false,
      // },
      // updateProps: {
      //   isRequired: true,
      //   isEditable: true,
      // },
      fieldProps: {
        exportPath: "state",
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
  ],
};

export { object as JournalEntryObject };
